import React, { Fragment, useState, useEffect } from 'react';
import InputText from '../../../Common/InputText';
import MultiSelectBox from '../../../Common/MultiSelectBox/MultiSelectBox';
import Notification from '../../../Common/Notification';
import { MAX_NUM_TASK_LINKS } from '../../../../constants/constants';

interface FileOption {
	id?: number;
	name: string;
	url: string;
	customerFileId?: string;
	baselineTaskLinkId?: number;
	[key: string]: any;
}

interface LinkComponentProps {
	baselineTask: object;
	onFormChange: Function;
	formFilters: {
		subscriptionFiles: FileOption[];
		[key: string]: any;
	};
	currentTask: {
		id?: number;
		links?: Array<any>;
		[key: string]: any;
	};
	filesActions?: {
		download: (fileName: string) => Promise<any>;
	};
}

export const LinksComponentForm = ({ baselineTask, onFormChange, formFilters, currentTask, filesActions }: LinkComponentProps) => {
	const [links, setLinks] = useState(currentTask?.links?.length ? [...currentTask.links] : []);
	const [selectedFile, setSelectedFile] = useState<FileOption>({} as FileOption);
	const [newLink, setNewLink] = useState({
		name: '',
		url: '',
		isActive: false,
		ordinal: 0,
		isFile: false,
	});
	const [editIndex, setEditIndex] = useState(null);

	useEffect(() => {
		setLinks(currentTask?.links?.length ? [...currentTask.links] : []);
	}, [currentTask.id]);

	const addLink = () => {
		if (links.length >= MAX_NUM_TASK_LINKS) {
			return;
		}

		if (newLink.name && newLink.url) {
			const ordinal = editIndex !== null ? editIndex + 1 : links.length + 1;
			const linkToAdd = {
				...newLink,
				id: 0,
				locationTaskId: currentTask?.id || 0,
				baselineTaskLinkId: null,
				customerFileId: null,
				isActive: true,
				ordinal: ordinal,
				isFile: false
			};
			const updatedLinks = [...links];
			if (editIndex !== null) {
				updatedLinks[editIndex] = linkToAdd;
				setEditIndex(null);
			} else {
				updatedLinks.push(linkToAdd);
			}
			setLinks(updatedLinks);
			setNewLink({ name: '', url: '', isActive: false, ordinal: 0, isFile: false });

			onFormChange('links', updatedLinks);
		}
	};

	const removeLink = (index) => {
		const updatedLinks = links.filter((_, i) => i !== index);
		setLinks(updatedLinks);
		onFormChange('links', updatedLinks);
	};

	const editLink = (index) => {
		setNewLink(links[index]);
		setEditIndex(index);
	};

	const addFileLink = () => {
		if (!selectedFile?.name || !selectedFile?.url || links.length >= MAX_NUM_TASK_LINKS) {
			return;
		}

		const newFileLink = {
			id: 0,
			locationTaskId: currentTask?.id || 0,
			baselineTaskLinkId: selectedFile.id || null,
			customerFileId: selectedFile.id || null,
			ordinal: links.length + 1,
			name: selectedFile.name,
			url: selectedFile.url,
			isActive: true,
			isFile: true,
			isEditing: false,
			isAdding: false
		};

		const updatedLinks = [...links, newFileLink];

		setLinks(updatedLinks);
		setSelectedFile({} as FileOption);

		onFormChange('links', updatedLinks);
	};

	const onDownloadLink = async (link, e) => {
		e.preventDefault();
		try {
			if (filesActions) {
				let start = link.url.indexOf('=') + 1;
				let fileName = link.url.slice(start);

				await filesActions.download(fileName);
				Notification('success', 'Download Started', 'File download has been initiated.');
			} else {
				Notification('error', 'Download Error', 'File download service is not available.');
			}
		} catch (error) {
			let errorMessage = 'An error occurred while downloading the file.';
			if (error instanceof Error && error.message) {
				errorMessage = error.message;
			}
			Notification('error', 'Download Failed', errorMessage);
		}
	};

	const onFileLinkChange = (url) => {
		const foundFile = formFilters.subscriptionFiles.find((m) => m.url === url);
		if (foundFile) {
			setSelectedFile(foundFile);
		}
	};

	return (
		<Fragment>
			<div className='col-6'>
				<div className='card'>
					<div className='card-header d-flex justify-content-between card-title'>
						<span>Links</span>
						<button type='button' className='btn btn-link' onClick={addLink}>
							<i className='fa-regular fa-circle-plus'></i>
							<span className='mx-1'>Add Link</span>
						</button>
					</div>
					<div className='card-body'>
						<div className='inner-container'>
							<div className='input-section'>
								<InputText
									name='linkName'
									type='text'
									placeholder='Name your link'
									className='form-control'
									value={newLink.name}
									onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
								/>
							</div>
							<div className='input-section links-component'>
								<div className='form-group'>
									<div className='input-group'>
										<div className='input-group-prepend'>
											<span className='input-group-text'>https://</span>
										</div>
										<input
											name='linkUrl'
											type='text'
											className='form-control input-text new-link-input'
											value={newLink.url}
											onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
										/>
										<div className='input-group-append'>
											<button type='button' className='btn link-confirm-btn' onClick={addLink}>
												<i className='fa-regular fa-check'></i>
											</button>
										</div>
									</div>
								</div>
							</div>
							<div className='footer' style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
								{links.map((link, index) => !link.isFile && (
									<div
										key={index}
										className='link-row'
									style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', minHeight: '18px', minWidth: 0 }}>
									<div className='link' style={{ flex: 1, paddingTop: '5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
										<a href={(link?.url ?? '').startsWith('http') ? (link?.url ?? '') : `https://${link?.url ?? ''}`} target="_blank" rel="noopener noreferrer" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link.name}</a>
										</div>
										<div className='link-actions' style={{ display: 'flex', gap: '10px' }}>
											<div className='icon-black' onClick={() => editLink(index)}>
												<i className='fa-light fa-pen-to-square'></i>
											</div>
											<div className='icon' onClick={() => removeLink(index)}>
												<span className='fa-light fa-trash-can'></span>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
			{/* SELECT EXISTING FILE */}
			<div className='col-6'>
				<div className='card'>
					<div className='card-header d-flex justify-content-between card-title'>
						<span>Files</span>
						<button type='button' className='btn btn-link' onClick={addFileLink}>
							<i className='fa-regular fa-circle-plus'></i>
							<span className='mx-1'>Add File</span>
						</button>
					</div>
					<div className='card-body'>
						<div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
							<div style={{ flex: 1, minWidth: 0 }}>
								<MultiSelectBox
									name='linkFile'
									label='Files'
									idField='url'
									valueField='name'
									className='mb-0'
									options={formFilters.subscriptionFiles}
									placeholder='Select existing file or add new'
									selectedValues={selectedFile?.url || ''}
									errorMessage='Is required'
									onChange={onFileLinkChange}
									isMulti={false}
								/>
							</div>
							<div>
								<button type='button' className='btn link-confirm-btn' onClick={addFileLink} title='Add selected file'>
									<i className='fa-regular fa-check'></i>
								</button>
							</div>
						</div>
						<div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
							{links.map((link, index) => link.isFile && (
								<div
									key={index}
									className='link-row'
									style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', minHeight: '18px' }}>
									<div className='link' style={{ flex: 1, paddingTop: '5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
										<button
											type='button'
											className='btn btn-link'
											onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDownloadLink(link, e); }}
											style={{ padding: 0, textAlign: 'left', textDecoration: 'none', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
										>
											{link.name}
										</button>
									</div>
									<div className='link-actions' style={{ display: 'flex', gap: '10px' }}>
										<div className='icon' onClick={() => removeLink(index)}>
											<span className='fa-light fa-trash-can'></span>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>

				</div>
			</div>
		</Fragment>
	);
};
